<script>
    import { fly } from 'svelte/transition';
	import { project_list } from '../../app-store';
	import ProjectUploader from './project-settings-uploader.svelte';
	import ProjectSettingsUrl from './project-settings-url.svelte';

	// Currently, receive the project object from the parent component
	export let project;
	export let onSubmit = () => {};

	let elementFormRef;

	const autofill_modes = {
		MANUAL_FORM : 0,
		UPLOAD : 1,
		URL : 2
	};
	let is_updating = project && project.project_ref ? true : false;

	// For new project, the default mode is URL
	// For existing project, the default mode is Manual
	let autofill_mode = is_updating ? autofill_modes.MANUAL_FORM : autofill_modes.URL;
	let error_submit = "";

	let form_data = project || {};
	form_data.config = project?.config || {};

	async function handleFormSubmit(event) {
		event.preventDefault();
		error_submit = "";

		const project_name = form_data.name?.trim();
		const project_config = {
			... form_data.config
		};

		if (!project_name ||
			!project_config.product_name ||
			!project_config.product_description ||
			!project_config.product_brand) {
			error_submit = "Please enter project name and product name, description and brand.";
			return;
		}

		if (!Array.isArray(project_config.target_reader) || !project_config.target_reader.length) {
			error_submit = "Please select at least one target reader.";
			return;
		}
		if (!Array.isArray(project_config.preferred_format) || !project_config.preferred_format.length) {
			error_submit = "Please select at least one preferred format.";
			return;
		}

		try {
			app.body_spinner.add_to(document.body);

			if (is_updating) {
				await app.lucep_ai.update_project(form_data.project_ref, project_name, project_config);

				project_list.update((projects) => {
					const index = projects.findIndex(p => p.project_ref === form_data.project_ref);
					if (index !== -1) {
						projects[index] = { ...projects[index], name: project_name, config: project_config };
					}
					return projects;
				});

				app.toast_msg.show("Project updated successfully");
			} else {
				await app.lucep_ai.create_project(project_name, project_config);

				project_list.update((projects) => {
					projects.push({
						project_ref: form_data.project_ref,
						name: project_name,
						config: project_config
					});
					return projects;
				});

				app.toast_msg.show("Project created successfully");
			}

			if (typeof onSubmit === 'function')
				onSubmit('ok');

		} catch (e) {
			error_submit = e.msg || "Failed to update the project. Please try again.";
		} finally {
			app.body_spinner.remove();
		}
	}

	function on_autofill_fetched(profile) {
		autofill_mode = autofill_modes.MANUAL_FORM;

		if (!profile) return;

		// Prioritize maintaining the existing data
		form_data = {
			...form_data,
			name : profile.project_name,
			config: {
				...form_data.config,
				... profile,
			}
		};

		// Select fields seems to requires reassigment in svelte
		if (!form_data.config.lang_code)
			form_data.config.lang_code = profile.lang_code;

		if (!form_data.config.writer_role)
			form_data.config.writer_role = profile.writer_role;

		if (!form_data.config.writer_tone)
			form_data.config.writer_tone = profile.writer_tone;

		// Arrays requires ressociation
		if (!form_data.config.target_reader?.length)
			form_data.config.target_reader = profile.target_reader;

		if (!form_data.config.preferred_format?.length)
			form_data.config.preferred_format = profile.preferred_format;

		if (form_data.lang_code === "en")
			form_data.config.lang_code = "en-US";
	}

	const transition_fly = { x: 100, duration: 200 };

</script>

<div class="mb-2">
	Auto-import from:
	<button class="btn btn-primary btn-small btn-group-left"
		class:selected={autofill_mode === autofill_modes.URL}
		on:click={() => autofill_mode = autofill_modes.URL}>
		🔗 Landing page URL
	</button>
	<button
		class="btn btn-primary btn-small btn-group-right"
		class:selected={autofill_mode === autofill_modes.UPLOAD}
		on:click={() => autofill_mode = autofill_modes.UPLOAD}>
		📤 Profile upload
	</button>
</div>

{#if autofill_mode == autofill_modes.URL}
	<div in:fly={transition_fly} class="field-product-name my-4">
		<ProjectSettingsUrl form_data={form_data} on_autofill_fetched={on_autofill_fetched} />
	</div>
	<div in:fly={{ y: 100, duration: 200 }} class="mt-4">
		<button class="btn btn-primary btn-small" on:click={() => autofill_mode = autofill_modes.MANUAL_FORM}>&#x276E;&nbsp; Manual form</button>
	</div>

{:else if autofill_mode == autofill_modes.UPLOAD}
	<div in:fly={transition_fly} class="my-4">
		<ProjectUploader on_autofill_fetched={on_autofill_fetched}  />
	</div>
	<div in:fly={{ y: 100, duration: 200 }} class="mt-4">
		<button class="btn btn-primary btn-small" on:click={() => autofill_mode = autofill_modes.MANUAL_FORM}>&#x276E;&nbsp; Manual form</button>
	</div>

{:else if autofill_mode == autofill_modes.MANUAL_FORM}
	<form bind:this={elementFormRef} in:fly={transition_fly} on:submit={handleFormSubmit} class="form-create-project">
		<div class="field-name mb-2">
			<label for="name" class="form-label">Project Name</label>
			<input bind:value={form_data.name} name="name" type="text" placeholder="Enter project name" class="form-control" >
		</div>
		<div class="field-product-name mb-2">
			<label for="page_url" class="form-label">Project Web Page URL</label>
			<input bind:value={form_data.config.page_url} name="url" type="url" class="form-control">
		</div>
		<div class="field-product-name mb-2">
			<label for="product_name" class="form-label">Product Name</label>
			<input bind:value={form_data.config.product_name} name="product_name" type="text" class="form-control">
		</div>
		<div class="field-product-name mb-2">
			<label for="product_description" class="form-label">Product Description</label>
			<textarea bind:value={form_data.config.product_description} name="product_description" class="form-control" rows="5" cols="40" maxlength="2048"></textarea>
		</div>
		<div class="field-product-name mb-2">
			<label for="product_brand" class="form-label">Product Brand</label>
			<input bind:value={form_data.config.product_brand} name="product_brand" type="text" class="form-control">
		</div>
		<div class="field-product-name mb-2">
			<label for="language">Language:</label>
			<select bind:value={form_data.config.lang_code} name="lang_code" class="form-control">
				<option value="" selected>Select...</option>
				<option value="en-US">English (US)</option>
				<option value="en-GB">English (UK)</option>
				<option value="en-SG">English (Singapore)</option>
				<option value="en-AU">English (Australia)</option>
				<option value="en-CA">English (Canada)</option>
				<option value="en-IN">English (India)</option>
				<option value="en-NZ">English (New Zealand)</option>
				<option value="en-ZA">English (South Africa)</option>
				<option value="zh">Mandarin Chinese</option>
				<option value="es">Spanish</option>
				<option value="hi">Hindi</option>
				<option value="id-ID">Indonesian</option>
				<option value="ms-MY">malaysian</option>
				<option value="ar">Arabic</option>
				<option value="bn">Bengali</option>
				<option value="pt">Portuguese</option>
				<option value="ru">Russian</option>
				<option value="ja">Japanese</option>
			</select>
		</div>
		<div class="field-product-name mb-2">
			<label for="writer_role">Writer role:</label>
			<select bind:value={form_data.config.writer_role} name="writer_role" class="form-control">
				<option value="" selected>Select...</option>
				<option value="scholar">Scholar</option>
				<option value="tutor">Tutor</option>
				<option value="specialist">Specialist</option>
				<option value="scientist">Scientist</option>
				<option value="journalist">Journalist</option>
				<option value="editor">Editor</option>
				<option value="novelist">Novelist</option>
				<option value="technical-writer">Technical Writer</option>
			</select>
		</div>
		<div class="field-product-name mb-2">
			<label for="writer_tone">Tone of voice:</label>
			<select bind:value={form_data.config.writer_tone} name="writer_tone" class="form-control">
				<option value="" selected>Select...</option>
				<option value="formal">Formal</option>
				<option value="casual">Casual</option>
				<option value="scientific">Scientific Article</option>
				<option value="genz">Gen Z</option>
				<option value="inspirational">Inspirational</option>
				<option value="humorous">Humorous</option>
				<option value="persuasive">Persuasive</option>
				<option value="storytelling">Storytelling</option>
				<option value="neutral">Neutral</option>
				<option value="authoritative">Authoritative</option>
				<option value="conversational">Conversational</option>
				<option value="poetic">Poetic</option>
			</select>
		</div>
		<div class="field-product-name mb-2">
			<label for="target-reader">Target readers:</label>
			<fieldset style="display: flex; gap: 2rem; flex-wrap: wrap;">
				<div style="flex: 1 1 150px;">
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="CEOs"> CEOs / Entrepreneurs</label><br>
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="IT-professionals"> IT Professionals</label><br>
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="RH-professionals"> HR Professionals</label><br>
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="consumers"> Consumers</label><br>
				</div>
				<div style="flex: 1 1 150px;">
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="developers"> Developers</label><br>
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="marketers"> Marketers</label><br>
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="parents"> Parents</label><br>
					<label><input bind:group={form_data.config.target_reader} type="checkbox" name="target-reader" value="students"> Students</label><br>
				</div>
			</fieldset>
		</div>
		<div class="field-product-name mb-2">
			<label for="preferred-format">Preferred formats:</label>
			<fieldset style="display: flex; gap: 2rem; flex-wrap: wrap;">
				<div style="flex: 1 1 150px;">
					<label><input bind:group={form_data.config.preferred_format} type="checkbox" name="preferred-format" value="technical-document"> Technical Document</label><br>
					<label><input bind:group={form_data.config.preferred_format} type="checkbox" name="preferred-format" value="executive-summary"> Executive Summary</label><br>
					<label><input bind:group={form_data.config.preferred_format} type="checkbox" name="preferred-format" value="F.A.Q."> FAQ</label><br>
				</div>
				<div style="flex: 1 1 150px;">
					<label><input bind:group={form_data.config.preferred_format} type="checkbox" name="preferred-format" value="step-by-step"> Step-by-Step Guide</label><br>
					<label><input bind:group={form_data.config.preferred_format} type="checkbox" name="preferred-format" value="dialog-script"> Dialog script</label><br>
				</div>
			</fieldset>
		</div>
		<!--
		<select name="industry" class="form-select">
			<option value="">Select Industry</option>
			<option value="technology">Technology</option>
			<option value="finance">Finance</option>
			<option value="healthcare">Healthcare</option>
			<option value="education">Education</option>
			<option value="manufacturing">Manufacturing</option>
			<option value="retail">Retail & E-commerce</option>
			<option value="real_estate">Real Estate</option>
			<option value="transportation">Transportation & Logistics</option>
			<option value="media">Media & Entertainment</option>
			<option value="government">Government</option>
		</select>
		-->
		<div class={"alert alert-primary alert-danger animate-in"} class:d-none={!error_submit}>
			{error_submit}
		</div>
		<button type="submit" class="btn btn-primary btn-full-width">
			{is_updating ? 'Update Project':'Create Project'}
		</button>
	</form>
{/if}

<style>
	.form-label	{
		margin-bottom: 0;
	}
	.btn-small {
		padding: 0.2rem .4rem;
		display: inline-block;
		font-size: 0.9rem;
	}
	.btn-small.selected {
		background-color: white;
		color: #7f5700;
	}
	.btn-group-left {
		border-bottom-right-radius: 0;
		border-top-right-radius: 0;
	}
	.btn-group-right {
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	}
</style>